import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { prints, INSTAGRAM_URL, numberWord } from '../content';
import { FadeUp, PageHeader } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const { lang, t } = useLanguage();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');
    const printIndex = prints.findIndex((p) => p.slug === ref);
    const print = printIndex >= 0 ? prints[printIndex] : null;
    const printLabel = print ? numberWord(printIndex + 1, lang) : '';

    const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (print) {
            setForm((f) => ({
                ...f,
                subject: `${t.contact.printInterest} « ${printLabel} »`,
                message: f.message || `${t.contact.printInterest} « ${printLabel} ».`,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, { ...form, reference: ref || null });
            toast.success(t.contact.success);
            setForm({ name: '', email: '', subject: '', message: '' });
        } catch (err) {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    const field =
        'w-full bg-transparent border-b border-white/20 py-4 text-paper placeholder:text-fog/50 focus:border-dusk focus:outline-none transition-colors duration-300';

    return (
        <main data-testid="contact-page">
            <PageHeader kicker={`Robin Spencer — ${t.nav.contact}`} title={t.contact.title} sub={t.contact.sub} />

            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1600px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-7">
                        <form onSubmit={handleSubmit} className="space-y-10" data-testid="contact-form">
                            {print && (
                                <div className="flex items-center gap-6 border border-white/10 bg-surface p-4" data-testid="contact-print-reference">
                                    <img src={`/images/${print.art}.jpg`} alt={printLabel} className="w-20 h-20 object-cover" />
                                    <div>
                                        <p className="text-xs tracking-[0.25em] uppercase text-dusk">{t.nav.shop}</p>
                                        <p className="font-serif italic text-xl text-paper mt-1 lowercase">{printLabel}</p>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                                <input
                                    required
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder={t.contact.name}
                                    className={field}
                                    data-testid="contact-name-input"
                                />
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder={t.contact.email}
                                    className={field}
                                    data-testid="contact-email-input"
                                />
                            </div>
                            <input
                                type="text"
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                placeholder={t.contact.subject}
                                className={field}
                                data-testid="contact-subject-input"
                            />
                            <textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder={t.contact.message}
                                className={`${field} resize-none`}
                                data-testid="contact-message-input"
                            />
                            <button
                                type="submit"
                                disabled={sending}
                                className="group inline-flex items-center gap-3 rounded-full bg-dusk px-8 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk-hover disabled:opacity-50"
                                data-testid="contact-submit-button"
                            >
                                {sending ? t.contact.sending : t.contact.send}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </form>
                    </FadeUp>

                    <FadeUp delay={0.15} className="lg:col-span-4 lg:col-start-9">
                        <div className="border border-white/10 bg-surface p-8 md:p-10" data-testid="contact-instagram-card">
                            <Instagram className="w-6 h-6 text-dusk mb-6" strokeWidth={1.5} />
                            <p className="text-sm text-fog leading-relaxed mb-8">{t.contact.instagram}</p>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-3 text-sm text-paper link-line"
                                data-testid="contact-instagram-link"
                            >
                                @robinspencer.artist
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </main>
    );
};

export default Contact;
